import apiClient, { getErrorMessage } from './api.service';
import { API_ENDPOINTS } from '../config/api';

export interface Booking {
  _id?: string;
  bookingId: string;
  userId: string;
  roomNumber: number;
  bedNumber: number;
  roomType: string;
  moveInDate: string;
  moveOutDate?: string;
  duration: number;
  pricePerMonth: number;
  totalCost: number;
  securityDeposit: number;
  status: 'pending' | 'confirmed' | 'paid' | 'checked-in' | 'completed' | 'cancelled';
  paymentStatus: 'pending' | 'partial' | 'paid';
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateBookingRequest {
  roomNumber: number;
  bedNumber: number;
  moveInDate: string;
}

export interface BookingResponse {
  status: string;
  message?: string;
  data: {
    booking: Booking;
  };
}

export interface BookingsResponse {
  status: string;
  count: number;
  data: {
    bookings: Booking[];
  };
}

export interface BookingStatsResponse {
  status: string;
  data: {
    stats: {
      totalBookings: number;
      activeBookings: number;
      cancelledBookings: number;
      completedBookings: number;
      totalRevenue: number;
      paidRevenue: number;
      pendingRevenue: number;
    };
  };
}

class BookingService {
  async createBooking(bookingData: CreateBookingRequest): Promise<Booking> {
    try {
      const response = await apiClient.post<BookingResponse>(
        API_ENDPOINTS.BOOKINGS.CREATE,
        bookingData
      );
      return response.data.data.booking;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  }

  async getAllBookings(): Promise<Booking[]> {
    try {
      const response = await apiClient.get<BookingsResponse>(
        API_ENDPOINTS.BOOKINGS.GET_ALL
      );
      return response.data.data.bookings;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  }

  async getBookingById(id: string): Promise<Booking> {
    try {
      const response = await apiClient.get<BookingResponse>(
        API_ENDPOINTS.BOOKINGS.GET_BY_ID(id)
      );
      return response.data.data.booking;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  }

  async cancelBooking(id: string, cancellationReason: string): Promise<Booking> {
    try {
      const response = await apiClient.delete<BookingResponse>(
        API_ENDPOINTS.BOOKINGS.CANCEL(id),
        { data: { cancellationReason } }
      );
      return response.data.data.booking;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  }

  async deleteBooking(id: string): Promise<void> {
    try {
      await apiClient.delete(
        API_ENDPOINTS.BOOKINGS.DELETE(id)
      );
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  }

  // Admin: force-free a bed by room + bed number. Works even when the bed's
  // booking record no longer exists (clears ghost occupancy).
  async releaseBed(
    roomNumber: number,
    bedNumber: number,
    cancellationReason?: string
  ): Promise<void> {
    try {
      await apiClient.delete(
        API_ENDPOINTS.BOOKINGS.RELEASE_BED(roomNumber, bedNumber),
        { data: { cancellationReason } }
      );
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  }

  async updateBookingStatus(
    id: string,
    status: string,
    paymentStatus?: string
  ): Promise<Booking> {
    try {
      const response = await apiClient.patch<BookingResponse>(
        API_ENDPOINTS.BOOKINGS.UPDATE_STATUS(id),
        { status, paymentStatus }
      );
      return response.data.data.booking;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  }
  
  async confirmPayment(
    id: string,
    moveInDate: string
  ): Promise<Booking> {
    try {
      const response = await apiClient.post<BookingResponse>(
        API_ENDPOINTS.BOOKINGS.CONFIRM_PAYMENT(id),
        { moveInDate }
      );
      return response.data.data.booking;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  }

  async updateBooking(
    id: string,
    updates: Partial<Pick<Booking, 'moveInDate'>>
  ): Promise<Booking> {
    try {
      const response = await apiClient.patch<BookingResponse>(
        API_ENDPOINTS.BOOKINGS.UPDATE_STATUS(id),
        updates
      );
      return response.data.data.booking;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  }

  async getBookingStats(): Promise<BookingStatsResponse['data']['stats']> {
    try {
      const response = await apiClient.get<BookingStatsResponse>(
        API_ENDPOINTS.BOOKINGS.GET_STATS
      );
      return response.data.data.stats;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  }
}

const bookingService = new BookingService();
export default bookingService;
